import { useState } from 'react';
import { useAccount } from 'wagmi';

import apiClient from 'src/agent/api/apiClient';
import { getWeb3Signer, signRequest } from 'src/utils/web3Signer';

interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

interface UploadResult {
  name: string;
  assetId: string;
  size: number;
}

interface UseNFTAssetUploadReturn {
  uploadFile: (file: File, programId: string) => Promise<UploadResult | null>;
  progress: UploadProgress;
  isUploading: boolean;
  error: string | null;
  reset: () => void;
}

export const useNFTAssetUpload = (): UseNFTAssetUploadReturn => {
  const { address } = useAccount();

  const [progress, setProgress] = useState<UploadProgress>({
    loaded: 0,
    total: 0,
    percentage: 0,
  });
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const uploadFile = async (file: File, programId: string): Promise<UploadResult | null> => {
    if (!address) {
      setError('Wallet not connected');
      return null;
    }

    setIsUploading(true);
    setError(null);
    setProgress({ loaded: 0, total: 0, percentage: 0 });

    try {
      // Get signer for signing the request
      const signer = getWeb3Signer();
      if (!signer) {
        throw new Error('Wallet not connected (signer missing)');
      }

      // Prepare form data
      const formData = new FormData();
      formData.append('file', file);

      // Sign the request
      const { signature, issuedAt } = await signRequest(formData);

      // Upload with real progress tracking
      const response = await apiClient<{
        data: {
          data: {
            file: {
              assetId: string;
              name: string;
              size: number;
            };
          };
        };
      }>({
        url: `/v1/programs/${programId}/nfts`,
        method: 'POST',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
          'x-nodesale-lang': 'en',
          'x-offchain-address': address,
          'x-offchain-signature': signature,
          'x-offchain-issued-at': issuedAt,
        },
        onUploadProgress: progressEvent => {
          const total = progressEvent.total || 0;
          const loaded = progressEvent.loaded || 0;
          const percentage = total > 0 ? Math.round((loaded * 100) / total) : 0;

          setProgress({
            loaded,
            total,
            percentage,
          });
        },
      });

      setIsUploading(false);
      const fileData = response.data.data.data.file;
      const result: UploadResult = {
        name: fileData.name,
        assetId: fileData.assetId,
        size: fileData.size,
      };
      return result;
    } catch (err: any) {
      setIsUploading(false);
      setError(err?.response?.data?.message || 'Upload failed. Please try again.');
      return null;
    }
  };

  const reset = () => {
    setProgress({ loaded: 0, total: 0, percentage: 0 });
    setIsUploading(false);
    setError(null);
  };

  return {
    uploadFile,
    progress,
    isUploading,
    error,
    reset,
  };
};
